import { ApplicationConfig, LOCALE_ID, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { MetaReducer, provideStore, StoreConfig } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { ingredientReducer } from './core/state/ingredient/ingredient.reducer';
import { environment } from '../environments/environment';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { authReducer } from './core/state/auth/auth.reducer';
import { AuthEffects } from './core/state/auth/auth.effects';
import { userReducer } from './core/state/user/user.reducer';
import { UserEffects } from './core/state/user/user.effects';
import { IngredientEffects } from './core/state/ingredient/ingredient.effects';
import { storageReducer } from './core/state/storage/storage.reducer';
import { StorageEffects } from './core/state/storage/storage.effects';
import { HouseholdEffects } from './core/state/household/household.effects';
import { householdReducer } from './core/state/household/households.reducer';
import { shoppingListReducer } from './core/state/shopping-list/shopping-list.reducer';
import { ShoppingListEffects } from './core/state/shopping-list/shopping-list.effects';
import { recipeReducer } from './core/state/recipe/recipe.reducer';
import { RecipeEffects } from './core/state/recipe/recipe.effects';
import { MealPlannerEffects } from './core/state/meal-planner/meal-planner.effects';
import { mealPlannerReducer } from './core/state/meal-planner/meal-planner.reducer';
import { leftoverReducer } from './core/state/leftover/leftover.reducer';
import { LeftoverEffects } from './core/state/leftover/leftover.effects';
import { FeedbackEffects } from './core/state/feedback/feedback.effects';
import { feedbackReducer } from './core/state/feedback/feedback.reducer';
import { announcementReducer } from './core/state/announcement/announcement.reducer';
import { AnnouncementEffects } from './core/state/announcement/announcement.effects';
import { AppClientInterceptor } from './core/interceptors/app-client.interceptor';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { versionReducer } from './core/state/version/version.reducer';
import { VersionEffects } from './core/state/version/version.effects';
import { imageReducer } from './core/state/image/image.reducer';
import { ImageEffects } from './core/state/image/image.effects';
import { ingredientAssociationReducer } from './core/state/ingredient-association/ingredient-association.reducer';
import { IngredientAssociationEffects } from './core/state/ingredient-association/ingredient-association.effects';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';

function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/i18n/', '.json');
}

function getInitialLocale(): string {
  const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('language') : null;
  return saved === 'hu' ? 'hu-HU' : 'en-GB';
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptorsFromDi()), // Provide HttpClientModule here
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AppClientInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: getInitialLocale() },
    { provide: MAT_DATE_LOCALE, useValue: getInitialLocale() },
    provideNativeDateAdapter(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    provideStore({ auth: authReducer, user: userReducer, ingredients: ingredientReducer, leftovers: leftoverReducer, household: householdReducer, storage: storageReducer, shoppingList: shoppingListReducer, recipes: recipeReducer, mealPlanner: mealPlannerReducer, feedback: feedbackReducer, announcements: announcementReducer, version: versionReducer, image: imageReducer, ingredientAssociations: ingredientAssociationReducer }),
    provideEffects(AuthEffects, UserEffects, IngredientEffects, StorageEffects, HouseholdEffects, ShoppingListEffects, RecipeEffects, MealPlannerEffects, LeftoverEffects, FeedbackEffects, AnnouncementEffects, VersionEffects, ImageEffects, IngredientAssociationEffects), 
    provideStoreDevtools({ maxAge: 25, logOnly: environment.production }),
  ]
};
